import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { paywallsApi } from '@/api';
import type { PaywallListParams } from '@/api/paywalls';
import { extractApiError, extractApiErrorWithTierNumbers } from '@/api/errorStatus';
import type { PaywallsState, Paywall } from '@/types';
import { preserveOwnerName } from './preserveOwnerName';
import { switchActiveOrgThunk } from './organizationsSlice';
import { switchActiveProjectThunk, clearActiveProjectThunk } from './projectsSlice';
import { rejectedApiError } from './rejectedApiError';

const initialState: PaywallsState = {
  paywalls: [],
  total: 0,
  isLoading: false,
  error: null,
  limit: 20,
  offset: 0,
};

/**
 * WEB-745: fetchPaywallsThunk now accepts pagination/search params. Backward
 * compatible with the bare-string call (`fetchPaywallsThunk(projectId)`) —
 * that form fetches with backend defaults (limit=20, offset=0).
 */
export type FetchPaywallsArg = string | ({ projectId: string } & PaywallListParams);

function normalizeFetchArg(arg: FetchPaywallsArg): { projectId: string } & PaywallListParams {
  return typeof arg === 'string' ? { projectId: arg } : arg;
}

export const fetchPaywallsThunk = createAsyncThunk(
  'paywalls/fetchAll',
  async (arg: FetchPaywallsArg, { rejectWithValue }) => {
    const { projectId, ...params } = normalizeFetchArg(arg);
    try {
      const response = await paywallsApi.list(projectId, params);
      return response.data as { paywalls: Paywall[]; total: number };
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(extractApiError(error));
      }
      return rejectWithValue('Failed to fetch paywalls');
    }
  }
);

export const createPaywallThunk = createAsyncThunk(
  'paywalls/create',
  async (
    { projectId, data }: { projectId: string; data: Record<string, unknown> },
    { rejectWithValue }
  ) => {
    try {
      const response = await paywallsApi.create(projectId, data);
      return response.data as Paywall;
    } catch (error: unknown) {
      // WEB-911: зеркало duplicatePaywallThunk — сохраняем статус/код/числа
      // лимита, иначе обычное создание (плашка «Создать пейволл» без шаблона)
      // теряло 402 TIER_LIMIT в голой строке и страница показывала человеку
      // тишину вместо предложения перейти на платный тариф (hold, дорожка 05.08).
      // WEB-1955: разбор отказа — одной дверью (`extractApiErrorWithTierNumbers`,
      // api/errorStatus.ts). Ручная сборка пейлоада теряла `reasonCode` —
      // причину денежного 402, по которой окно тарифа называет, что именно
      // кончилось, — и клала `code: null` вместо «кода нет», отчего
      // страховочная ветка «402 без кода» в utils/tierLimit.ts была
      // недостижима.
      return rejectWithValue(extractApiErrorWithTierNumbers(error, 'Failed to create paywall'));
    }
  }
);

export const duplicatePaywallThunk = createAsyncThunk(
  'paywalls/duplicate',
  async (
    { projectId, id }: { projectId: string; id: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await paywallsApi.duplicate(projectId, id);
      return response.data as Paywall;
    } catch (error: unknown) {
      // WEB-911: зеркало квизового дублирования — сохраняем статус/код/числа
      // лимита, иначе страница покажет техническую ошибку вместо предложения
      // перейти на платный тариф.
      return rejectWithValue(extractApiErrorWithTierNumbers(error, 'Failed to duplicate paywall'));
    }
  }
);

export const deletePaywallThunk = createAsyncThunk(
  'paywalls/delete',
  async (
    { projectId, id }: { projectId: string; id: string },
    { rejectWithValue }
  ) => {
    try {
      await paywallsApi.delete(projectId, id);
      return id;
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(extractApiError(error));
      }
      return rejectWithValue('Failed to delete paywall');
    }
  }
);

export const publishPaywallThunk = createAsyncThunk(
  'paywalls/publish',
  async (
    { projectId, id }: { projectId: string; id: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await paywallsApi.publish(projectId, id);
      return response.data as Paywall;
    } catch (error: unknown) {
      // QA 2026-07-17: реджектим extractApiError (объект {status,message}), а не
      // error.message — иначе серверное сообщение (напр. NO_DOMAIN_BOUND «Привяжите
      // домен…») теряется, и таблица показывает generic «Unexpected error».
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const unpublishPaywallThunk = createAsyncThunk(
  'paywalls/unpublish',
  async (
    { projectId, id }: { projectId: string; id: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await paywallsApi.unpublish(projectId, id);
      return response.data as Paywall;
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const movePaywallThunk = createAsyncThunk(
  'paywalls/move',
  async (
    { projectId, id, targetProjectId }: { projectId: string; id: string; targetProjectId: string },
    { rejectWithValue }
  ) => {
    try {
      await paywallsApi.move(projectId, id, targetProjectId);
      return id;
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(extractApiError(error));
      }
      return rejectWithValue('Failed to move paywall');
    }
  }
);

export const copyPaywallThunk = createAsyncThunk(
  'paywalls/copy',
  async (
    { projectId, id, targetProjectId }: { projectId: string; id: string; targetProjectId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await paywallsApi.copy(projectId, id, targetProjectId);
      return response.data as Paywall;
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(extractApiError(error));
      }
      return rejectWithValue('Failed to copy paywall');
    }
  }
);

export const updatePaywallThunk = createAsyncThunk(
  'paywalls/update',
  async (
    { projectId, id, data }: { projectId: string; id: string; data: Record<string, unknown> },
    { rejectWithValue }
  ) => {
    try {
      const response = await paywallsApi.update(projectId, id, data);
      return response.data as Paywall;
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(extractApiError(error));
      }
      return rejectWithValue('Failed to update paywall');
    }
  }
);

const paywallsSlice = createSlice({
  name: 'paywalls',
  initialState,
  reducers: {
    clearError(state) {
      return { ...state, error: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaywallsThunk.pending, (state, action) => ({
        ...state,
        isLoading: true,
        error: null,
        // Ф3 (ревизия десятки 3, 15.08): requestId-гард — см. PaywallsState.latestRequestId.
        latestRequestId: action.meta?.requestId ?? null,
      }))
      .addCase(fetchPaywallsThunk.fulfilled, (state, action) => {
        // Ф3: a stale (superseded) response landing late must not overwrite
        // the fresher list already on screen (mirrors quizzesSlice).
        if (state.latestRequestId != null && action.meta.requestId !== state.latestRequestId) {
          return state;
        }
        const { limit, offset } = normalizeFetchArg(action.meta.arg);
        return {
          ...state,
          isLoading: false,
          paywalls: action.payload.paywalls,
          total: action.payload.total,
          limit: limit ?? 20,
          offset: offset ?? 0,
        };
      })
      .addCase(fetchPaywallsThunk.rejected, (state, action) => {
        // Ф3: same out-of-order guard as fulfilled above.
        if (state.latestRequestId != null && action.meta.requestId !== state.latestRequestId) {
          return state;
        }
        return {
          ...state,
          isLoading: false,
          error: rejectedApiError(action),
        };
      })
      .addCase(createPaywallThunk.fulfilled, (state, action) => ({
        ...state,
        paywalls: [...state.paywalls, action.payload],
        total: state.total + 1,
      }))
      .addCase(duplicatePaywallThunk.fulfilled, (state, action) => ({
        ...state,
        paywalls: [...state.paywalls, action.payload],
        total: state.total + 1,
      }))
      .addCase(deletePaywallThunk.fulfilled, (state, action) => ({
        ...state,
        paywalls: state.paywalls.filter((p) => p.id !== action.payload),
        total: state.total - 1,
      }))
      .addCase(publishPaywallThunk.fulfilled, (state, action) => ({
        ...state,
        paywalls: state.paywalls.map((p) =>
          p.id === action.payload.id ? preserveOwnerName(p, action.payload) : p
        ),
      }))
      .addCase(updatePaywallThunk.fulfilled, (state, action) => ({
        ...state,
        paywalls: state.paywalls.map((p) =>
          p.id === action.payload.id ? preserveOwnerName(p, action.payload) : p
        ),
      }))
      .addCase(unpublishPaywallThunk.fulfilled, (state, action) => ({
        ...state,
        paywalls: state.paywalls.map((p) =>
          p.id === action.payload.id ? preserveOwnerName(p, action.payload) : p
        ),
      }))
      // WEB-4114 (сверка вида 31.10, К2 № 2): отказ снятия с публикации НЕ
      // пишет `error` — это поле отказа загрузки списка, и страница по нему
      // заменяла всю таблицу на «Не удалось загрузить …». Отказ снятия
      // показывает всплывашка страницы (WEB-1370 п.1, `.unwrap()`), список
      // остаётся.
      .addCase(movePaywallThunk.fulfilled, (state, action) => ({
        ...state,
        paywalls: state.paywalls.filter((p) => p.id !== action.payload),
        total: state.total - 1,
      }))
      // WEB-1058: см. funnelsSlice — пейволлы, как и воронки, живут внутри
      // проекта организации; переключение активной организации оставляло
      // пейволлы прежней организации в сторе (видны в Контент → Пейволлы до
      // F5, а фильтр «Владелец» держал людей прежней организации).
      .addCase(switchActiveOrgThunk.fulfilled, (state) => ({
        ...state,
        paywalls: initialState.paywalls,
        total: initialState.total,
        error: initialState.error,
        limit: initialState.limit,
        offset: initialState.offset,
      }))
      // WEB-1337 P1: mirror of switchActiveOrgThunk.fulfilled above, for
      // switching PROJECT within the same org (or entering org-mode) — the
      // paywall list is scoped to a project too.
      .addCase(switchActiveProjectThunk.fulfilled, (state) => ({
        ...state,
        paywalls: initialState.paywalls,
        total: initialState.total,
        error: initialState.error,
        limit: initialState.limit,
        offset: initialState.offset,
      }))
      .addCase(clearActiveProjectThunk.fulfilled, (state) => ({
        ...state,
        paywalls: initialState.paywalls,
        total: initialState.total,
        error: initialState.error,
        limit: initialState.limit,
        offset: initialState.offset,
      }));
    // copyPaywallThunk: no state change — the copy lands in another project
  },
});

export const { clearError } = paywallsSlice.actions;
export default paywallsSlice.reducer;
